import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { CircleStop, GripHorizontal, Mic, SquarePenIcon, TrashIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  apiPutStoryQuestionById,
  APIPutStoryQuestionPayload,
  StoryQuestion,
} from '@/shared/api';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import { cn, INPUT_DEBOUNCE_TIME } from '@/shared/utils';

import { DeleteStoryQuestionDialog } from './delete-story-question-dialog';
import { EditableTitle } from './editable-title';
import { StoryQuestionItemAudioRecorder } from './story-question-item-audio-recorder';

type StoryQuestionItemProps = {
  storyId: string;
  guest?: boolean;
} & StoryQuestion;

export const StoryQuestionItem = ({
  answer,
  content,
  id,
  storyId,
  guest = false,
  audioAnswer,
}: StoryQuestionItemProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    data: {
      whoami: StoryQuestionItem.name,
    },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  /**
   * TODO: check if the user is story creator.
   * We don't need to allow creators to answer on the questions.
   */
  const [_answer, _setAnswer] = useState(answer);

  const debounceAnswerInputTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceContentInputTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateStoryQuestionMutation = useMutation<
    StoryQuestion,
    Error,
    APIPutStoryQuestionPayload
  >({
    mutationFn: (updatedStoryQuestion) => {
      return apiPutStoryQuestionById(String(id), updatedStoryQuestion).then(
        ({ data }) => data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storyByIdQueryOptions(storyId).queryKey,
      });
    },
  });

  const handleChangeAnswer = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;

    _setAnswer(event.target.value);

    if (debounceAnswerInputTimeoutRef.current) {
      clearTimeout(debounceAnswerInputTimeoutRef.current);
    }

    debounceAnswerInputTimeoutRef.current = setTimeout(() => {
      updateStoryQuestionMutation.mutate({ answer: value });
    }, INPUT_DEBOUNCE_TIME);
  };

  const handleChangeContent = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (debounceContentInputTimeoutRef.current) {
      clearTimeout(debounceContentInputTimeoutRef.current);
    }

    debounceContentInputTimeoutRef.current = setTimeout(() => {
      updateStoryQuestionMutation.mutate({ content: value });
    }, INPUT_DEBOUNCE_TIME);
  };

  const author = !guest;

  const hasAnswer = _answer !== null || audioAnswer !== null;
  const noAnswer = _answer === null && audioAnswer === null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild className="cursor-default">
          <div className="group" ref={setNodeRef} style={style} {...attributes}>
            {/* TODO: Introduce AFTER MVP. Check Backend notes */}
            {/* <div
              className="flex w-full cursor-move items-center justify-center"
              {...listeners}
            >
              <GripHorizontal size={16} className="invisible group-hover:visible" />
            </div> */}

            <div className="flex items-center justify-between">
              <EditableTitle
                readonly={guest || hasAnswer}
                initialTitle={content}
                trigger={<SquarePenIcon className="h-4 w-4 cursor-pointer" />}
                onChange={handleChangeContent}
                placeholder={intl.formatMessage({ id: 'input.question.placeholder' })}
              >
                <Label
                  className={cn('text-left text-base', {
                    'text-gray-400': !content,
                    'text-muted-foreground': _answer && !guest,
                  })}
                  htmlFor={`question-${id}`}
                >
                  {content ?? intl.formatMessage({ id: 'input.question.placeholder' })}
                </Label>
              </EditableTitle>

              {author && noAnswer && (
                <DeleteStoryQuestionDialog questionId={id} storyId={storyId}>
                  <Button variant="ghost" size="icon">
                    <TrashIcon />
                  </Button>
                </DeleteStoryQuestionDialog>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <div className="mt-2 flex items-center gap-2">
                <Textarea
                  readOnly={author}
                  onChange={handleChangeAnswer}
                  id={`question-${id}`}
                  value={_answer ?? ''}
                  placeholder={
                    guest ? intl.formatMessage({ id: 'textarea.answer.placeholder' }) : ''
                  }
                  className={cn('max-h-64', {
                    'text-muted-foreground': !guest,
                    'cursor-default': hasAnswer,
                    'pointer-events-none cursor-default': author,
                  })}
                />
              </div>

              <StoryQuestionItemAudioRecorder
                audioAnswer={audioAnswer}
                id={id}
                storyId={storyId}
                guest={guest}
              />
            </div>
          </div>
        </TooltipTrigger>

        {hasAnswer && author && (
          <TooltipContent>
            {intl.formatMessage({ id: 'storyQuestion.noEdit.tooltip' })}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};
